import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api/apiClient";
import { jwtDecode } from "jwt-decode";


const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldClearCache, setShouldClearCache] = useState(true);
  const navigate = useNavigate();

  // Clean logout function
  const logout = () => {
    localStorage.removeItem("access_token");
    setUser(null);
    navigate("/login");
  };

  // Automatically logout when token expires
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const expTime = decoded.exp * 1000; // Convert to ms
      const currentTime = Date.now();
      const timeUntilExpiry = expTime - currentTime;
      console.log("Time until expiry:", timeUntilExpiry);
      if (timeUntilExpiry <= 0) {
        logout();
      } else {
        const timeoutId = setTimeout(() => {
          logout();
        }, timeUntilExpiry);

        return () => clearTimeout(timeoutId); // Cleanup
      }
    } catch (err) {
      console.error("Invalid token format:", err);
      logout();
    }
  }, [user]); // Rerun if `user` changes (e.g., after login)

  // Initial auth check (only on mount)
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        const response = await apiClient.get("/v1/auth/verify", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.user); // Adjust if needed: maybe `response.data.user`
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Login handler
  const login = async (email, password) => {
    try {
      setError(null);
      const response = await apiClient.post("/v1/auth/login", {
        email,
        password,
      });

      const { access_token, user } = response;

      localStorage.setItem("access_token", access_token);
      const onboarding_user = {...user, right: 'ADMIN', is_superuser: false}
      setUser(user.right!=="ONBOARDING"?user: onboarding_user);
      return { success: true };
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail || "Login failed. Please try again.";
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuthStatus,
    isAuthenticated: !!user,
    shouldClearCache,
    setShouldClearCache,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
